import { KeywayService, MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { Keyway, KeywayType } from '@overtur-monorepo/keying-shared'
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { fadeIn } from '../../../animations/fadeIn'
import { Cylinder, KeySystem, Structure } from '../../../models/dev.test.models'
import { ErrorService } from '../../../services/error.service'
import { KeyingBookService } from '../../../services/keying.book.service'
import { KeyingCylinderService } from '../../../services/keying.cylinder.service'
import { KeyingDataSetsService } from '../../../services/keying.data.sets.service'
import { LoadingService } from '../../../services/loading.service'
import { EventListenerFocusTrapInertStrategy } from '@angular/cdk/a11y'

@Component({
    selector: 'app-cylinder-pinning-matrix',
    templateUrl: './cylinder-pinning-matrix.component.html',
    styleUrls: ['./cylinder-pinning-matrix.component.scss'],
    animations: [fadeIn],
    standalone: false
})
export class CylinderPinningMatrixComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  @Input() structure: Structure = null
  @Input() keySystem: KeySystem = null
  @Input() selectedCylinder: Cylinder
  @Input() selectedMasterKey: MasterKey = null

  @Output() selectedKeyWayId = new EventEmitter<any>()

  cylinderKeyways: Keyway[] = []

  bittingDataSets
  matrix
  columns
  allKeyWaysInFamily

  defaultKeyway: Keyway
  selectedKeyWay: Keyway
  defaultKwyName

  sampleData = {
    masterCombination: [2, 5, 0, 7, 6, 3],
    kba: [
      [4, 5, 6, 2, 5, 2],
      [4, 5, 6, 2, 5, 2],
      [4, 5, 6, 2, 5, 2],
      [4, 5, 6, 2, 5, 2],
    ],
    sop: [4, 5, 6, 2, 5, 2],
    shearLine: [
      [4, 5, 6, 2, 5, 2],
      [4, 5, 6, 2, 5, 2],
    ],
  }

  constructor(
    public keyingDataSetsService: KeyingDataSetsService,
    private keywayService: KeywayService,
    public keyingBookService: KeyingBookService,
    private errorService: ErrorService,
    private loadingService: LoadingService,
    private keyingCylinderService: KeyingCylinderService
  ) {}

  ngOnInit(): void {
    this.keyingBookService.bittingDataSets.pipe(takeUntil(this.destroy$)).subscribe((bittingData) => {
      if (!bittingData) {
        return
      }
      this.bittingDataSets = bittingData
      this.matrix = this.bittingDataSets.matrix
      this.columns = this.matrix.masterKeyBitting.length
    })
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.selectedCylinder?.currentValue) {
      this.loadKeyways()
    }
  }

  selectKeyway(keyway) {
    this.selectedKeyWay = this.allKeyWaysInFamily.find((kwy) => kwy.name === keyway)
    this.selectedCylinder.keywayId = this.selectedKeyWay.id
    this.selectedKeyWayId.emit(this.selectedKeyWay.id)
  }

  private loadKeyways() {
    let cylinderKeywayId = this.selectedCylinder.keywayId
    let keywayFamilyId = this.structure.keywayFamilyId

    this.keywayService
      .getAllKeyways(keywayFamilyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (keyways) => {
          this.allKeyWaysInFamily = keyways
          this.cylinderKeyways = []
          let allCyclinderOperatorskeywayIds = [] // Collect all the keyway Id's for the cyclinder operators
          let allOprKeyway = [] // collect all keyways from the keyway family Id API call
          let plugorCyclinderkeyWays = [] // fillter plug or cyclinder keyways from the operator cyclinder
          let elevatedKeyWays = [] // fillter elevated keyways from the operator cyclinder

          this.selectedCylinder.operators.forEach((opr) => {
            if (
              opr.keywayId !== null &&
              allCyclinderOperatorskeywayIds.every((oprKeyWays) => oprKeyWays !== oprKeyWays?.keywayId)
            ) {
              allCyclinderOperatorskeywayIds.push(opr.keywayId)
            }
          })
          if (allCyclinderOperatorskeywayIds.length > 0) {
            allCyclinderOperatorskeywayIds.forEach((keyWayId) => {
              const filteredKeyWays = keyways.filter((kwy) => kwy.id === keyWayId)
              allOprKeyway = allOprKeyway.concat(filteredKeyWays)
            })
          }

          plugorCyclinderkeyWays = allOprKeyway.filter(
            (plugCycKeyWay) => plugCycKeyWay.keywayTypeId !== KeywayType.Elevated
          )

          if (plugorCyclinderkeyWays.length > 0) {
            plugorCyclinderkeyWays.forEach((keyWay) => {
              this.cycOperatorskeyWays(keyWay)
            })
          } else {
            elevatedKeyWays = allOprKeyway.filter((elvKeyWay) => elvKeyWay.keywayTypeId === KeywayType.Elevated)
            const nodeLevels = elevatedKeyWays.map((keyWayLevel) => keyWayLevel.nodeLevel)
            const leastnodeLevel = Math.min(...nodeLevels)
            elevatedKeyWays = allOprKeyway.filter((kwyNode) => kwyNode.nodeLevel === leastnodeLevel)
            elevatedKeyWays.forEach((keyWayElvChild) => {
              plugorCyclinderkeyWays = keyWayElvChild.childKeyways.filter(
                (keyWayChild) => keyWayChild.keywayTypeId !== KeywayType.Elevated
              )
              if (plugorCyclinderkeyWays.length > 0) {
                plugorCyclinderkeyWays.forEach((keyWayPlugCyc) => {
                  this.cycOperatorskeyWays(keyWayPlugCyc)
                })
              }
            })
          }

          if (this.cylinderKeyways.length > 1) {
            this.cylinderKeyways.sort((a, b) => (a.name > b.name ? 1 : -1))
            this.defaultKeyway = this.cylinderKeyways.find((defaultkeyWay) => defaultkeyWay.id === cylinderKeywayId)
          } else if (this.cylinderKeyways.length === 1) {
            this.cylinderKeyways.sort((a, b) => (a.name > b.name ? 1 : -1))
            this.defaultKeyway = this.cylinderKeyways.find((defaultkeyWay) => defaultkeyWay.id === cylinderKeywayId)
          }
          this.defaultKwyName = this.defaultKeyway?.name
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  private cycOperatorskeyWays(keyWay) {
    if (this.cylinderKeyways.every((cycKeyway) => cycKeyway.id !== keyWay.id)) {
      this.cylinderKeyways.push(keyWay)
      keyWay.childKeyways.forEach((kwy) => {
        this.cycOperatorskeyWays(kwy)
      })
    }
  }
}
